import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Chip, CircularProgress,
  Alert, Card, CardContent, CardActions, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog,
  DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckIcon, RadioButtonUnchecked as UncheckIcon,
  Receipt as ReceiptIcon, CreditCard as CreditCardIcon,
  OpenInNew as OpenIcon, SensorsOutlined as WAGIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Plano {
  id: string;
  codigo: string;
  nome: string;
  descr: string;
  precoMensal: number;
  precoAnual: number;
  maxAgencias: number;
  maxServicos: number;
  maxOperadores: number;
  smsEnabled: string;
  features: string[];
}

interface Subscription {
  id: string;
  planoCodigo: string;
  planoNome: string;
  ciclo: string;
  preco: number;
  estado: string;
  dtInicio: string;
  dtFim: string;
  dtProximoPagt: string;
  autoRenovar: string;
  features: string[];
}

interface Fatura {
  id: string;
  planoCodigo: string;
  ciclo: string;
  valor: number;
  periodoInicio: string;
  periodoFim: string;
  estado: string;
  pagaliRef: string;
  dtEmissao: string;
  dtPagamento?: string;
}

const planoCor: Record<string, string> = {
  FREE: '#9CA3AF', BASIC: '#3B82F6', PRO: '#8B5CF6', ENTERPRISE: '#F59E0B',
};

const estadoFatCor: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  PAGO: 'success', FALHADO: 'error', PENDENTE: 'warning', CANCELADO: 'default',
};

const WAGBilling: React.FC = () => {
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ciclo, setCiclo] = useState<'MENSAL' | 'ANUAL'>('MENSAL');
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [planoSelecionado, setPlanoSelecionado] = useState<Plano | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planosRes, subRes, fatRes] = await Promise.all([
        api.get('/wag/billing/planos'),
        api.get('/admin/wag/billing/subscription'),
        api.get('/admin/wag/billing/faturas'),
      ]);
      setPlanos(planosRes.data.data || []);
      setSubscription(subRes.data.data);
      setFaturas(fatRes.data.data || []);
    } catch {
      setError('Erro ao carregar dados de billing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSubscribe() {
    if (!planoSelecionado) return;
    setSubscribing(true);
    try {
      const res = await api.post('/admin/wag/billing/subscribe', {
        planoCodigo: planoSelecionado.codigo,
        ciclo,
      });
      if (res.data.data.requiresPayment && res.data.data.paymentUrl) {
        window.open(res.data.data.paymentUrl, '_blank');
      }
      setSubscribeOpen(false);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao iniciar subscrição');
    } finally {
      setSubscribing(false);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      await api.post('/admin/wag/billing/cancel');
      setCancelOpen(false);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao cancelar');
    } finally {
      setCancelling(false);
    }
  }

  const preco = (p: Plano) => ciclo === 'ANUAL' ? p.precoAnual : p.precoMensal;
  const desconto = (p: Plano) => p.precoMensal > 0 && p.precoAnual > 0
    ? Math.round((1 - (p.precoAnual / (p.precoMensal * 12))) * 100) : 0;

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <WAGIcon sx={{ color: '#e94560' }} />
        <Typography variant="h5" fontWeight={700}>Planos & Billing</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Gerencie a sua subscrição WAG e consulte o historial de facturas
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Subscrição actual */}
      {subscription && (
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3, borderLeft: `4px solid ${planoCor[subscription.planoCodigo] || '#e94560'}` }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Plano actual</Typography>
              <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                <Typography variant="h5" fontWeight={700}>{subscription.planoNome}</Typography>
                <Chip label={subscription.estado} size="small"
                  color={subscription.estado === 'ACTIVA' ? 'success' : subscription.estado === 'TRIAL' ? 'info' : 'warning'} />
                <Chip label={subscription.ciclo} size="small" variant="outlined" />
              </Box>
              {subscription.dtFim && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {subscription.estado === 'ACTIVA' ? 'Renova em' : 'Expira em'}: {new Date(subscription.dtFim).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="contained" size="small" onClick={() => setSubscribeOpen(true)}
                sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
                Alterar plano
              </Button>
              {subscription.planoCodigo !== 'FREE' && (
                <Button variant="outlined" size="small" color="error" onClick={() => setCancelOpen(true)}>
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {!subscription && (
        <Alert severity="info" sx={{ mb: 4 }} action={
          <Button size="small" onClick={() => setSubscribeOpen(true)}>Escolher plano</Button>
        }>
          Ainda não tem uma subscrição activa. Escolha um plano para começar.
        </Alert>
      )}

      {/* Grelha de planos */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h6" fontWeight={600}>Planos disponíveis</Typography>
        <ToggleButtonGroup value={ciclo} exclusive onChange={(_, v) => v && setCiclo(v)} size="small">
          <ToggleButton value="MENSAL">Mensal</ToggleButton>
          <ToggleButton value="ANUAL">
            Anual
            <Chip label="poupa até 17%" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: 10 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3} mb={5}>
        {planos.map(p => {
          const isActual = subscription?.planoCodigo === p.codigo;
          const color = planoCor[p.codigo] || '#e94560';
          const desc = desconto(p);
          return (
            <Grid item xs={12} sm={6} md={3} key={p.id}>
              <Card sx={{
                borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
                border: isActual ? `2px solid ${color}` : '2px solid transparent',
                boxShadow: isActual ? 4 : 1,
              }}>
                <CardContent sx={{ flex: 1 }}>
                  {isActual && <Chip label="Actual" size="small" sx={{ mb: 1, bgcolor: color + '22', color }} />}
                  <Typography variant="h6" fontWeight={700} color={color}>{p.nome}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>{p.descr}</Typography>
                  <Box mb={2}>
                    {p.precoMensal === 0 && p.codigo === 'FREE' ? (
                      <Typography variant="h4" fontWeight={900}>Grátis</Typography>
                    ) : p.codigo === 'ENTERPRISE' ? (
                      <Typography variant="h5" fontWeight={700}>Sob consulta</Typography>
                    ) : (
                      <>
                        <Typography variant="h4" fontWeight={900}>
                          {preco(p).toLocaleString('pt-CV')} <Typography component="span" variant="body2" color="text.secondary">CVE</Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          /{ciclo === 'ANUAL' ? 'ano' : 'mês'}
                          {ciclo === 'ANUAL' && desc > 0 && ` (${desc}% de desconto)`}
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List dense disablePadding>
                    {(Array.isArray(p.features) ? p.features : JSON.parse(p.features as any || '[]')).map((f: string) => (
                      <ListItem key={f} disablePadding sx={{ py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon sx={{ fontSize: 16, color }} />
                        </ListItemIcon>
                        <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {p.codigo === 'ENTERPRISE' ? (
                    <Button fullWidth variant="outlined" href="mailto:wag@pagali.cv">Contactar</Button>
                  ) : (
                    <Button fullWidth variant={isActual ? 'outlined' : 'contained'} disabled={isActual}
                      onClick={() => { setPlanoSelecionado(p); setSubscribeOpen(true); }}
                      sx={!isActual ? { bgcolor: color, '&:hover': { bgcolor: color + 'dd' } } : {}}>
                      {isActual ? 'Plano actual' : 'Escolher'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Histórico de facturas */}
      {faturas.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ReceiptIcon color="action" />
            <Typography variant="h6" fontWeight={600}>Historial de Facturas</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Referência</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faturas.map(f => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.pagaliRef}</TableCell>
                    <TableCell><Chip label={f.planoCodigo} size="small" sx={{ bgcolor: (planoCor[f.planoCodigo] || '#e94560') + '22', color: planoCor[f.planoCodigo] || '#e94560' }} /></TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(f.periodoInicio).toLocaleDateString('pt-CV', { timeZone: 'UTC' })} → {new Date(f.periodoFim).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{Number(f.valor).toLocaleString('pt-CV')} CVE</TableCell>
                    <TableCell><Chip label={f.estado} size="small" color={estadoFatCor[f.estado] || 'default'} /></TableCell>
                    <TableCell><Typography variant="caption">{new Date(f.dtEmissao).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Dialog confirmar subscrição */}
      <Dialog open={subscribeOpen} onClose={() => setSubscribeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar subscrição</DialogTitle>
        <DialogContent>
          {planoSelecionado && (
            <Box>
              <Typography>
                Plano: <strong>{planoSelecionado.nome}</strong> ({ciclo})
              </Typography>
              {planoSelecionado.precoMensal > 0 && planoSelecionado.codigo !== 'ENTERPRISE' && (
                <Typography mt={1}>
                  Valor: <strong>{preco(planoSelecionado).toLocaleString('pt-CV')} CVE/{ciclo === 'ANUAL' ? 'ano' : 'mês'}</strong>
                </Typography>
              )}
              {planoSelecionado.precoMensal > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Será redireccionado para o Pagali para concluir o pagamento.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscribeOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubscribe} disabled={subscribing}
            startIcon={<CreditCardIcon />}
            sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
            {subscribing ? 'A processar…' : planoSelecionado?.precoMensal === 0 ? 'Activar grátis' : 'Pagar com Pagali'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog cancelar */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs">
        <DialogTitle>Cancelar subscrição?</DialogTitle>
        <DialogContent>
          <Typography>O plano será revertido para <strong>Free</strong> no final do período actual. Os seus dados não serão eliminados.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Manter plano</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? 'A cancelar…' : 'Confirmar cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WAGBilling;
